<template>
  <!-- «Cómo hacer buenas fotos» (docs/tutorial-video-buenas-fotos.md): a full screen of the tutorials' family,
       the bar (close, the name, the time left) over the video and its progress. Nothing to seek: the bar
       only tells how far along it is. -->
  <section
    class="pv"
    data-testid="photo-video-tutorial"
    :data-state="state"
    :aria-labelledby="titleId"
  >
    <header class="pv-bar">
      <BzButton
        class="pv-exit"
        kind="secondary"
        size="md"
        icon="close"
        :label="$t('message.photoVideoTutorialClose')"
        data-testid="photo-video-close"
        @click="close"
      />
      <span :id="titleId" class="pv-bar-title">{{ $t('message.photoVideoTutorialTitle') }}</span>
      <span class="pv-remaining" data-testid="photo-video-remaining">
        <BzIcon name="schedule" class="pv-remaining-icon" />
        {{ $t('message.photoVideoTutorialRemaining', { time: remainingClock }) }}
      </span>
    </header>

    <div class="pv-main">
      <!-- The frame and its progress, together and centred: in portrait the bar stays under the video. -->
      <div ref="fit" class="pv-fit">
        <div class="pv-frame" :style="frameStyle">
          <video
            ref="video"
            class="pv-video"
            :src="video.url"
            playsinline
            preload="auto"
            data-testid="photo-video-player"
            @loadedmetadata="onMetadata"
            @playing="onPlaying"
            @pause="onPause"
            @waiting="onWaiting"
            @progress="onProgress"
            @timeupdate="onTimeUpdate"
            @ended="onEnded"
            @error="onError"
          ></video>

          <!-- A tap anywhere on the video pauses it, or plays it when it is paused. -->
          <button
            v-if="state === 'playing' || state === 'paused'"
            type="button"
            class="pv-tap"
            :aria-label="state === 'playing' ? $t('message.photoVideoTutorialPause') : $t('message.photoVideoTutorialPlay')"
            data-testid="photo-video-tap"
            @click="toggle"
          ></button>

          <div v-if="state === 'loading' || state === 'buffering'" class="pv-overlay pv-overlay--veil" data-testid="photo-video-loading">
            <BzIcon name="progress_activity" class="pv-spinner bz-spinner" />
            <span class="pv-overlay-text">{{ $t('message.photoVideoTutorialLoading') }}</span>
          </div>

          <!-- Paused: carry on, or watch it again from the start (the end screen's pair, play first). -->
          <div v-else-if="state === 'paused'" class="pv-overlay pv-overlay--veil" @click.self="play">
            <div class="pv-actions">
              <BzButton kind="secondary" size="lg" icon="replay" :label="$t('message.photoVideoTutorialRestart')"
                        data-testid="photo-video-restart" @click="replay"/>
              <BzButton kind="primary" size="lg" icon="play_arrow" :label="$t('message.photoVideoTutorialPlay')"
                        data-testid="photo-video-play" @click="play"/>
            </div>
          </div>

          <div v-else-if="state === 'ended'" class="pv-overlay pv-overlay--scrim" data-testid="photo-video-ended">
            <div class="pv-actions">
              <BzButton kind="secondary" size="lg" icon="replay" :label="$t('message.photoVideoTutorialReplay')"
                        data-testid="photo-video-replay" @click="replay"/>
              <BzButton kind="primary" size="lg" icon="check" :label="$t('message.photoVideoTutorialClose')"
                        data-testid="photo-video-done" @click="close"/>
            </div>
          </div>

          <div v-else-if="state === 'error'" class="pv-overlay pv-overlay--scrim bz-empty" data-testid="photo-video-error">
            <BzIcon name="wifi_off" class="bz-empty__icon" />
            <p class="bz-empty__title">{{ $t('message.photoVideoTutorialErrorTitle') }}</p>
            <p class="bz-empty__text">{{ $t('message.photoVideoTutorialErrorBody') }}</p>
            <BzButton class="pv-retry" kind="primary" size="lg" icon="refresh" :label="$t('message.photoVideoTutorialRetry')"
                      data-testid="photo-video-retry" @click="retry"/>
          </div>
        </div>

        <div
          ref="progress"
          class="pv-progress"
          :style="progressStyle"
          role="progressbar"
          :aria-labelledby="titleId"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-valuenow="Math.round(progress * 100)"
          data-testid="photo-video-progress"
        >
          <i class="pv-progress-fill" :style="{ transform: `scaleX(${progress})` }"></i>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
/**
 * PhotoVideoTutorialScreen — plays «Cómo hacer buenas fotos» (docs/tutorial-video-buenas-fotos.md).
 *
 * App.vue opens it from Ajustes with the video of the running language (`photoVideo`, from
 * src/photoVideoTutorial.js) and takes it back to Ajustes on `photo-video-close`. Everything about the
 * playing lives here, and so does the telemetry: this screen is the one that knows how much was watched.
 *
 * A plain `<video>`, not a library player: the controls are the design system's (close, the time left, tap
 * to pause, a progress that does not seek), and the file streams from S3 by byte ranges. It starts with
 * sound: the tap on the Ajustes row is the gesture that allows it. Where the browser still refuses
 * (WebKit, the tap a frame too far), it waits paused behind its play button.
 *
 * Leaving stops the download: the source is dropped on unmount, so a closed video costs no more data.
 */
import BzButton from '@/ui/components/BzButton.vue';
import BzIcon from '@/ui/components/BzIcon.vue';
import { formatClock, trackPhotoVideo, MAX_WATCH_STEP_S } from '@/photoVideoTutorial';

/** Waiting this long without a byte arriving is a network that is not coming back by itself. */
const STALL_MS = 20000;
const DEFAULT_RATIO = 16 / 9;

let uid = 0;

export default {
  name: 'PhotoVideoTutorialScreen',
  components: { BzButton, BzIcon },
  props: {
    photoVideo: { type: Object, required: true },
    // Where it was opened from, for the telemetry: 'settings' (the Ajustes row) or 'link' (support's link).
    photoVideoSource: { type: String, default: 'settings' },
  },
  emits: ['photo-video-close'],
  data() {
    uid += 1;
    return {
      titleId: `pv-title-${uid}`,
      // loading · playing · paused · buffering · ended · error
      state: 'loading',
      duration: this.photoVideo.durationS,
      currentTime: 0,
      ratio: DEFAULT_RATIO,
      frameW: 0,
      frameH: 0,
      // What the closed event reports.
      plays: 0,
      watchedS: 0,
      maxPct: 0,
      completed: false,
    };
  },
  computed: {
    video() {
      return this.photoVideo;
    },
    remainingClock() {
      return formatClock(Math.max(0, this.duration - this.currentTime));
    },
    progress() {
      if (!this.duration) return 0;
      return Math.min(1, Math.max(0, this.currentTime / this.duration));
    },
    frameStyle() {
      return this.frameW ? { width: `${this.frameW}px`, height: `${this.frameH}px` } : {};
    },
    progressStyle() {
      return this.frameW ? { width: `${this.frameW}px` } : {};
    },
  },
  created() {
    // Not reactive on purpose: the playhead at the last `timeupdate`, whether the next `playing` starts a
    // play from the top, and the stall watchdog.
    this._lastTime = 0;
    this._fromTop = true;
    this._stallTimer = null;
    this._resumeAt = 0;
  },
  mounted() {
    this.measure();
    if (typeof ResizeObserver !== 'undefined') {
      this._observer = new ResizeObserver(() => this.measure());
      this._observer.observe(this.$refs.fit);
    } else {
      window.addEventListener('resize', this.measure);
    }
    document.addEventListener('visibilitychange', this.onVisibility);
    trackPhotoVideo('shown', this.baseProps());
    this.armStall();
    this.play();
  },
  beforeUnmount() {
    // Here and not in close(): a session that expires or a reload from Ayuda unmounts it too, and the
    // watching it saw still counts.
    trackPhotoVideo('closed', {
      ...this.baseProps(),
      watched_s: Math.round(this.watchedS),
      max_pct: this.maxPct,
      plays: this.plays,
      completed: this.completed,
    });
    clearTimeout(this._stallTimer);
    if (this._observer) this._observer.disconnect();
    window.removeEventListener('resize', this.measure);
    document.removeEventListener('visibilitychange', this.onVisibility);
    const el = this.$refs.video;
    if (el) {
      try {
        el.pause();
        el.removeAttribute('src');
        el.load();
      } catch (_) { /* the element is going away anyway */ }
    }
  },
  methods: {
    baseProps() {
      return { locale: this.video.locale, video: this.video.file, source: this.photoVideoSource };
    },

    // ---- the frame: the video's own shape, as large as the space allows ----
    measure() {
      const el = this.$refs.fit;
      if (!el) return;
      // The bar and the gap above it come out of the height the frame may take.
      const bar = this.$refs.progress;
      const gap = parseFloat(window.getComputedStyle(el).rowGap) || 0;
      const w = el.clientWidth;
      const h = el.clientHeight - (bar ? bar.offsetHeight : 0) - gap;
      if (!w || h <= 0) return;
      const width = Math.min(w, h * this.ratio);
      this.frameW = Math.floor(width);
      this.frameH = Math.floor(width / this.ratio);
    },

    // ---- what the person does ----
    play() {
      const el = this.$refs.video;
      if (!el) return;
      let attempt;
      try {
        attempt = el.play();
      } catch (_) {
        this.state = 'paused';
        return;
      }
      // Autoplay refused: wait behind the play button instead of a spinner that never ends.
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch((err) => {
          if (err && err.name === 'NotAllowedError') this.state = 'paused';
        });
      }
    },
    toggle() {
      const el = this.$refs.video;
      if (!el) return;
      if (this.state === 'playing') el.pause();
      else this.play();
    },
    // «Ver otra vez» at the end and «Desde el principio» in a pause: back to 0:00, counted as a new play.
    replay() {
      const el = this.$refs.video;
      if (!el) return;
      this._fromTop = true;
      this._lastTime = 0;
      el.currentTime = 0;
      this.currentTime = 0;
      this.state = 'loading';
      // As retry(): a network that died during the pause must end in «Reintentar», not an endless spinner.
      this.armStall();
      this.play();
    },
    retry() {
      const el = this.$refs.video;
      if (!el) return;
      this.state = 'loading';
      // Still downloading (a slow network the watchdog gave up on): keep what it has and just ask again.
      // Otherwise start the load over, and go back to where it stopped once it knows its length
      // (onMetadata); a retry that failed before that keeps the position of the first.
      if (el.networkState !== el.NETWORK_LOADING) {
        this._resumeAt = this._resumeAt || this.currentTime;
        el.load();
      }
      this.armStall();
      this.play();
    },
    close() {
      this.$emit('photo-video-close');
    },

    // ---- what the video says ----
    onMetadata() {
      const el = this.$refs.video;
      if (!el) return;
      if (Number.isFinite(el.duration) && el.duration > 0) this.duration = el.duration;
      if (this._resumeAt > 0) {
        try { el.currentTime = this._resumeAt; } catch (_) { /* from the start, then */ }
        this._lastTime = this._resumeAt;
        this._resumeAt = 0;
      }
      if (el.videoWidth && el.videoHeight) {
        this.ratio = el.videoWidth / el.videoHeight;
        this.measure();
      }
    },
    onPlaying() {
      clearTimeout(this._stallTimer);
      this.state = 'playing';
      if (this._fromTop) {
        this._fromTop = false;
        this.plays += 1;
        trackPhotoVideo('played', { ...this.baseProps(), play: this.plays });
      }
    },
    onPause() {
      const el = this.$refs.video;
      // `ended` fires a `pause` first in some engines: the end is not a pause. A pause the browser makes
      // while it loads (the screen locked) is one too: behind the play button, not a spinner that turns
      // into a false network error.
      if (!el || !el.paused || el.ended) return;
      if (this.state === 'playing' || this.state === 'buffering' || this.state === 'loading') {
        clearTimeout(this._stallTimer);
        this.state = 'paused';
      }
    },
    onWaiting() {
      if (this.state === 'playing') this.state = 'buffering';
      this.armStall();
    },
    onProgress() {
      // Bytes are still arriving: a slow network, not a dead one.
      if (this.state === 'buffering' || this.state === 'loading') this.armStall();
    },
    armStall() {
      clearTimeout(this._stallTimer);
      this._stallTimer = setTimeout(() => {
        if (this.state === 'buffering' || this.state === 'loading') this.state = 'error';
      }, STALL_MS);
    },
    onTimeUpdate() {
      const el = this.$refs.video;
      if (!el) return;
      const t = el.currentTime;
      const step = t - this._lastTime;
      if (step > 0 && step <= MAX_WATCH_STEP_S) this.watchedS += step;
      this._lastTime = t;
      this.currentTime = t;
      if (this.duration) this.maxPct = Math.max(this.maxPct, Math.min(100, Math.round((t / this.duration) * 100)));
    },
    onEnded() {
      clearTimeout(this._stallTimer);
      this.state = 'ended';
      this.currentTime = this.duration;
      this.maxPct = 100;
      this.completed = true;
      trackPhotoVideo('completed', { ...this.baseProps(), play: this.plays });
    },
    onError() {
      clearTimeout(this._stallTimer);
      // Dropping the source on unmount raises an error too: nobody is looking any more.
      if (!this.$refs.video || !this.$refs.video.getAttribute('src')) return;
      this.state = 'error';
    },
    onVisibility() {
      // Screen off or another app on top: no voice talking to an empty kitchen.
      const busy = this.state === 'playing' || this.state === 'buffering' || this.state === 'loading';
      if (document.hidden && busy && this.$refs.video) this.$refs.video.pause();
    },
  },
};
</script>

<style scoped>
/* On the design system: only its tokens, its buttons and icons, its spinner and empty state. The shell is
   the tutorials' (QrGunTutorialScreen.vue): a bar, then the content. */
.pv {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background: var(--bz-bg);
  color: var(--bz-ink);
  font-family: var(--bz-font);
  font-synthesis: none;
  font-kerning: normal;
  isolation: isolate;
}

/* ---- the bar: close, the name, the time left ---- */
.pv-bar {
  flex: none;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: var(--bz-space-4);
  box-sizing: border-box;
  min-height: var(--bz-bar-height);
  padding: env(safe-area-inset-top) max(var(--bz-space-6), env(safe-area-inset-right)) 0 max(var(--bz-space-6), env(safe-area-inset-left));
  border-bottom: 1px solid var(--bz-edge);
}

.pv-exit {
  justify-self: start;
}

.pv-bar-title {
  font-size: var(--bz-text-lg);
  font-weight: var(--bz-weight-label);
  line-height: var(--bz-leading-lg);
  letter-spacing: var(--bz-tracking-lg);
  text-align: center;
}

.pv-remaining {
  justify-self: end;
  display: flex;
  align-items: center;
  gap: var(--bz-space-2);
  color: var(--bz-ink-2);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  letter-spacing: var(--bz-tracking-md);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.pv-remaining-icon {
  font-size: var(--bz-icon-sm);
}

/* ---- the video and its progress ---- */
.pv-main {
  flex: 1;
  min-height: 0;
  display: flex;
  box-sizing: border-box;
  padding: var(--bz-gutter) max(var(--bz-gutter), env(safe-area-inset-right)) max(var(--bz-gutter), env(safe-area-inset-bottom)) max(var(--bz-gutter), env(safe-area-inset-left));
}

.pv-fit {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--bz-space-4);
}

.pv-frame {
  position: relative;
  overflow: hidden;
  max-width: 100%;
  max-height: 100%;
  border-radius: var(--bz-radius-lg);
  background: var(--bz-surface);
}

.pv-video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.pv-tap {
  position: absolute;
  inset: 0;
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.pv-tap:focus-visible {
  outline: 3px solid var(--bz-focus);
  outline-offset: -3px;
}

.pv-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--bz-space-4);
}

/* Paused and loading: the frame stays visible under a light veil. */
.pv-overlay--veil {
  background: var(--bz-veil);
}

/* The end and the error: the words and buttons are what matters now. */
.pv-overlay--scrim {
  background: var(--bz-scrim);
}

.pv-spinner {
  font-size: var(--bz-icon-lg);
}

.pv-overlay-text {
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  letter-spacing: var(--bz-tracking-md);
}

.pv-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--bz-target-gap);
}

.pv-retry {
  margin-top: var(--bz-space-4);
}

.pv-progress {
  flex: none;
  position: relative;
  overflow: hidden;
  max-width: 100%;
  height: var(--bz-slider-track);
  border-radius: var(--bz-radius-full);
  background: var(--bz-control);
}

.pv-progress-fill {
  position: absolute;
  inset: 0;
  transform-origin: left center;
  background: var(--bz-track-active);
}

/* `timeupdate` comes about four times a second: the fill glides between them instead of stepping. */
@media (prefers-reduced-motion: no-preference) {
  .pv-progress-fill {
    transition: transform var(--bz-duration) linear;
  }
}
</style>
